<template>
  <div class="pb-20 lg:py-20">
    <loading-indicator v-if="loading"></loading-indicator>

    <div v-else>
      <div
        class="container grid grid-cols-1 px-10 sm:grid-cols-2 lg:px-14 xl:px-24"
      >
        <div class="mt-12 lg:mt-0">
          <home-title-hover-line color="home-yellow" />
          <h1 class="mx-auto text-home-3xl font-bold lg:text-home-5xl">
            {{ location.vendor.public_name }}
          </h1>
          <div class="mt-3">
            <i class="fas fa-map-marker-alt mr-2"></i>
            <a
              :href="googleMapsURL"
              target="_blank"
              class="cursor-pointer font-bold underline underline-offset-4 hover:text-primary-500 hover:underline"
              >{{ location.street }}, {{ location.zip }} {{ location.city }}</a
            >
          </div>

          <div class="grid lg:grid-cols-2">
            <div class="mt-12 gap-y-4 lg:mt-14">
              <div
                v-for="(categoryGroup, index) in categoriesGroupedByTwo"
                :key="index"
                class="mb-4 grid grid-cols-2 gap-4 md:flex md:gap-0"
              >
                <div
                  v-for="category in categoryGroup"
                  :key="category.id"
                  class="inline-flex items-center gap-3 md:min-w-[200px] md:gap-4"
                >
                  <img
                    class="h-7"
                    :src="getIconUrl(category)"
                    :alt="category._translations[$i18n.locale].name"
                  />
                  <span class="text-sm">{{
                    category._translations[$i18n.locale].name
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <plain-button
            style="box-shadow: 0px 4px 37px rgba(0, 0, 0, 0.25)"
            class="mt-12 max-sm:w-full lg:mt-14"
            border-color="primary-500"
            size="home"
            @click="bookNow()"
            >{{ $t('button.bookAppointment') }}</plain-button
          >
        </div>
        <div
          v-if="vendorPictures.length > 0"
          class="order-first container sm:order-0 sm:mt-12"
        >
          <swiper-container
            ref="swiperInstance"
            data-cy="vendor-pictures"
            :slides-per-view="swiperConfig.slidesPerView"
            :space-between="swiperConfig.spaceBetween"
            :pagination="false"
            :navigation="true"
            class="-mx-10 aspect-square rounded-br-[100px] sm:mx-0 sm:aspect-3/4 sm:rounded-[50px] lg:aspect-4/3"
            @swiperslidechange="onSlideChange"
            @swiperinit="onInit"
          >
            <swiper-slide
              v-for="(picture, index) in vendorPictures"
              :key="picture"
              class="flex h-full w-full items-center justify-center overflow-hidden rounded-br-[100px] bg-gray-200 sm:rounded-[50px]"
            >
              <optimized-image
                :src="picture"
                :alt="`${location.vendor.public_name} - Image ${index + 1}`"
                :width="1200"
                :height="1200"
                :priority="index === 0"
                sizes="(max-width: 640px) 120vw, (max-width: 1023px) 100vw, 50vw"
                container-class="h-full w-full"
              />
            </swiper-slide>
          </swiper-container>
          <div
            v-if="vendorPictures.length > 1"
            class="mt-4 flex justify-center gap-4"
          >
            <div
              v-for="(bullet, index) in bullets"
              :key="index"
              data-cy="vendor-picture-bullet"
              class="h-2.5 w-2.5 rounded-full"
              :class="[bullet.active ? 'bg-home-yellow' : 'bg-home-gray-300']"
            ></div>
          </div>
        </div>
      </div>

      <div class="mt-12 py-4 md:container md:px-10 lg:px-14 xl:px-24">
        <div
          class="flex scrollbar-none space-x-6 overflow-x-auto border-b-2 border-home-gray-100 px-10 md:space-x-8 md:px-0"
        >
          <div
            v-for="(tab, index) in tabs"
            :key="index"
            style="flex: 0 0 auto"
            class="cursor-pointer font-bold uppercase"
          >
            <router-link
              v-slot="{ href, navigate, isActive }"
              :to="tab.to"
              custom
              replace
            >
              <a
                :href="href"
                :class="[
                  'block border-b-2 pb-2',
                  isActive
                    ? 'border-home-yellow text-home-gray-800'
                    : 'border-transparent text-home-gray-500 hover:text-home-gray-800',
                ]"
                @click="navigate"
                >{{ $t(tab.buttonLabel) }}</a
              >
            </router-link>
          </div>
        </div>
      </div>

      <div class="mt-4 md:container md:px-10 lg:px-14 xl:px-24">
        <router-view></router-view>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, provide, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useLocationsStore } from '@/store/locations-store';
import type { Location } from '@/model/location';
import LoadingIndicator from '@/components/shared/LoadingIndicator.vue';
import type { Category } from '@/model/category';
import uniqBy from 'lodash/uniqBy';
import { useBookingsStore } from '@/store/bookings-store';
import { useUsersStore } from '@/store/users-store';
import HomeTitleHoverLine from '@/components/public/HomeTitleHoverLine.vue';
import { useCategory } from '@/composables/category';
import entitiesService from '@/services/entities.service';
import { Collection } from '@/store/common/collections';
import { useServicesStore } from '@/store/services-store';
import { Service } from '@/model/service';
import { useSwiper } from '@/composables/useSwiper';
import 'swiper/element/css/pagination';
import OptimizedImage from '@/components/shared/OptimizedImage.vue';

const route = useRoute();
const locationsStore = useLocationsStore();
const servicesStore = useServicesStore();
const loading = ref(true);
const vendorPictures = ref<Array<string>>([]);

// Use the Swiper composable
const { bullets, swiperInstance, swiperConfig, onInit, onSlideChange } =
  useSwiper({
    slidesPerView: 1,
    spaceBetween: 30,
    pagination: false,
  });
// Location
const location = computed(() => {
  return locationsStore.getOpenLocations().find((location: Location) => {
    return (
      location.slug === route.params.location_slug &&
      location.vendor.slug === route.params.vendor_slug
    );
  });
});
provide('location', location);
const googleMapsURL = computed(() => {
  return (
    'https://www.google.com/maps/search/?api=1&query=' +
    location.value.street +
    ', ' +
    location.value.zip +
    ' ' +
    location.value.city
  );
});

// Services & Categories
const services: Ref<Service[]> = ref();
const locatedServices = computed(() => {
  return servicesStore.filterServicesForChosenLocation(
    location.value.id,
    services.value,
  );
});
provide('locatedServices', locatedServices);
const categories: ComputedRef<Category[]> = computed(() => {
  return uniqBy(
    services.value.map((service) => service.category),
    'id',
  );
});
const categoriesGroupedByTwo: ComputedRef<Category[][]> = computed(() => {
  return categories.value.reduce((acc, category, index) => {
    if (index % 2 === 0) {
      acc.push([category]);
    } else {
      acc[acc.length - 1].push(category);
    }
    return acc;
  }, [] as Category[][]);
});
const { getIconUrl } = useCategory();

const tabs = ref([
  {
    to: { name: 'VendorTabAbout' },
    buttonLabel: 'button.about',
  },
  {
    to: { name: 'VendorTabServices' },
    buttonLabel: 'bookingStepPlace.services',
  },
  {
    to: { name: 'VendorTabContact' },
    buttonLabel: 'button.contact',
  },
]);

const descriptionExists = computed(() => {
  return location.value.vendor.description !== null;
});

// Misc
const bookingsStore = useBookingsStore();
const router = useRouter();
const { isStaffOrMore } = useUsersStore();
const bookNow = () => {
  if (Object.keys(bookingsStore.booking.bookingInEdition).length > 0) {
    bookingsStore.resetBookingAddForm();
  }
  if (isStaffOrMore) {
    router.push({
      name: 'AddBooking',
    });
  } else {
    bookingsStore.setVendorId(location.value.vendor_id);
    bookingsStore.setVendorSlug(location.value.vendor.slug);
    router.push({
      name: 'PublicBooking',
      params: {
        vendorId: location.value.vendor_id.toString(10),
      },
    });
  }
};

const isChildRoute = computed(() => route.matched.length > 1);

onMounted(async () => {
  loading.value = true;
  await locationsStore.findAllPublic();
  let push;
  if (descriptionExists.value) {
    push = () => {
      router.push({ name: 'VendorTabAbout', replace: true });
    };
  } else {
    tabs.value = tabs.value.filter((tab) => tab.buttonLabel !== 'button.about');
    push = () => {
      router.push({ name: 'VendorTabServices', replace: true });
    };
  }
  if (!isChildRoute.value) {
    await push();
  }

  try {
    services.value = await entitiesService.findAll(
      Collection.SERVICES,
      location.value.vendor.id,
    );

    vendorPictures.value = location.value.vendor.pictures || [];
  } catch (exception) {
    console.error(exception);
  } finally {
    loading.value = false;
  }
});
</script>
